import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Category, getCategoryDisplayName } from '../../../../../shared/issuesV2';
import useDispatchOnMount from '../../../../hooks/useDispatchOnMount';
import * as SavedFiltersModule from '../../../../redux/savedFilters';
import { selectOptionFactory } from '../../../../utils';
import {
  canEditDefaultLicensingFilter,
  canEditDefaultQualityFilter,
  canEditDefaultSecurityFilter,
  canSetDefaultLicensingPolicy,
  canSetDefaultQualityPolicy,
  canSetDefaultSecurityPolicy,
} from '../../shared/permissions';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import type { NamedFilter } from '../../../../../shared/savedFilters/common';
import type { ReduxStore } from '../../../../redux/store';
import type { SelectOption } from '../../../../types';

import {
  convertSettingsFromGeneric,
  convertSettingsSliceFromGeneric,
  convertSettingsToGeneric,
  getEndpoint,
} from './utils';
import type { AnyIssueSettings, GenericIssueSettings } from './utils';

const UNSET_POLICY_OPTION = selectOptionFactory(null, 'Select policy');
const UNSET_FILTER_OPTION = selectOptionFactory(null, 'All issues (not filtered)');

type IssueSettingsPolicies = {
  //  SelectOption<null> is the option which allows the user to unset the policy.
  policyOptions: Array<SelectOption<null> | SelectOption<number>>;
  selectedPolicyOption: SelectOption<null> | SelectOption<number>;
  selectedPolicyName?: string;
};

/**
 * Manages the state for the policy selector, including
 * pulling in the org policies and setting up SelectOptions
 * for each policy.
 */
const useIssueSettingsPolicies = (
  policyOptions: Array<SelectOption<number | null>>,
  policyId?: number,
): IssueSettingsPolicies => {
  const selectedPolicyOption = policyOptions.find(({ value }) => policyId === value) || UNSET_POLICY_OPTION;

  return {
    policyOptions,
    selectedPolicyOption,
    //  This is used in the propagate modal, so we pull it out here if it exists.
    selectedPolicyName: policyId ? selectedPolicyOption.label : undefined,
  };
};

type IssueSettingsFilters = {
  //  SelectOption<null> is the option which allows the user to unset the filter.
  filterOptions: Array<SelectOption<null> | SelectOption<number>>;
  selectedFilterOption: SelectOption<null> | SelectOption<number>;
  selectedFilterName?: string;
};

/**
 * Manages the state for the filter selector, including
 * pulling in the org policies and setting up SelectOptions for each
 */
const useIssueSettingsFilters = (category: Category, filterId?: number): IssueSettingsFilters => {
  //  Fetch the list of saved filters once on mount.
  useDispatchOnMount(() => SavedFiltersModule.actions.getFilters(category));

  //  Get the saved filters from the state.
  const filters = useSelector<ReduxStore>((state) =>
    SavedFiltersModule.selectors.getFiltersByCategory(state, category),
  ) as NamedFilter[];
  const filterOptions = [
    //  This is what allows users to "unset" the fitler
    UNSET_FILTER_OPTION,
    ...filters.map((filter) => selectOptionFactory(filter.id, filter.name)),
  ];
  const selectedFilterOption = filterOptions.find(({ value }) => filterId === value) || UNSET_FILTER_OPTION;

  return {
    filterOptions,
    selectedFilterOption,
    selectedFilterName: filterId ? selectedFilterOption.label : undefined,
  };
};

//
const usePermissionToEditPolicyOrFilter = (category: Category): {
  canEditPolicy: boolean;
  canEditFilters: boolean;
} => {
  if (category === Category.Licensing) {
    return {
      canEditPolicy: canSetDefaultLicensingPolicy(),
      canEditFilters: canEditDefaultLicensingFilter(),
    };
  }
  if (category === Category.Vulnerability) {
    return {
      canEditPolicy: canSetDefaultSecurityPolicy(),
      canEditFilters: canEditDefaultSecurityFilter(),
    };
  }
  if (category === Category.Quality) {
    return {
      canEditPolicy: canSetDefaultQualityPolicy(),
      canEditFilters: canEditDefaultQualityFilter(),
    };
  }
  //  This should never happen but just to guard, we return the expected structure.
  return {
    canEditPolicy: false,
    canEditFilters: false,
  };
};

/**
 * Hook for fetching the default settings for issues in projects.
 */
const useIssueSettings = (
  orgId: number,
  category: Category,
  policyOptions: Array<SelectOption<null | number>> = [],
) => {
  const categoryDisplayName = getCategoryDisplayName(category);
  const {
    settings: nonGenericSettings,
    isLoading,
    isDirty,
    errors,
    onUpdateSettings,
    onSaveSettingsWithOverride,
  } = useOrganizationSettings<AnyIssueSettings>({
    endpoint: getEndpoint(orgId, category),
    settingsSection: `project ${categoryDisplayName} issues`,
  });

  //  Normalize the settings so that all of the categories use the same field names.
  const genericSettings = convertSettingsToGeneric(category, nonGenericSettings);

  const policies = useIssueSettingsPolicies(policyOptions, genericSettings?.policyId ?? undefined);
  const filters = useIssueSettingsFilters(category, genericSettings?.filterId ?? undefined);

  const { canEditPolicy, canEditFilters } = usePermissionToEditPolicyOrFilter(category);

  //  Because the settings are normalized to GenericIssueSettings, we'll want to convert them back
  //  to what they were before (ie: their API/AnyIssueSettings format).
  const onUpdate = useCallback(
    (updatedSettings: Partial<GenericIssueSettings>) =>
      onUpdateSettings(convertSettingsSliceFromGeneric(category, updatedSettings)),
    [category, onUpdateSettings],
  );

  const onSave = useCallback(() => {
    if (genericSettings) {
      //  Convert the settings from the normalized/generic version to the category-specific version.
      const anyIssueSettings = convertSettingsFromGeneric(category, genericSettings);
      //  onSaveSettingsWithOverride accepts a "dataOverride" prop which will be used instead of the settings in state.
      onSaveSettingsWithOverride(anyIssueSettings);
    }
  }, [genericSettings, category, onSaveSettingsWithOverride]);

  return {
    settings: genericSettings,
    isLoading,
    errors,
    policies,
    filters,
    isSaveButtonDisabled: !isDirty || isLoading.any,
    onUpdateSettings: onUpdate,
    onSaveSettings: onSave,
    canEditPolicy,
    canEditFilters,
  };
};

export default useIssueSettings;
